import { inject, Injectable } from '@angular/core';
import { BADGES_COLORS } from '@catalogues';
import { BadgeConfig, BaseBoundaryDates, Milestone, MilestoneStatus } from '@models';
import { CommonFunctionsService } from '@services';

@Injectable({
  providedIn: 'root'
})
export class MilestoneTooltipService {
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);

  buildHTML(milestone: Milestone): string {
    return `
        <div class="study-gantt-chart-tooltip-container">
          <div class="study-gantt-chart-tooltip-title">${milestone.nameTooltip}</div>
          <div class="study-gantt-chart-tooltip-contents">
              ${this.getStatusHTML(milestone.status)}
              ${this.getDatesHTML(milestone.values)}
              ${this.getDeltaHTML(milestone.delta)}
          </div>
        </div>
      `;
  }

  private getStatusHTML(milestoneStatus: MilestoneStatus): string {
    const badge: BadgeConfig = this.getMilestoneBadge(milestoneStatus);

    return `
     <div class="study-gantt-chart-tooltip-status-item">
        <span class="study-gantt-chart-tooltip-label">Status:</span>
        <core-badge 
          color="${badge.textColor}" 
          backgroundcolor="${badge.backgroundColor ?? ''}" 
          label="${badge.label}" 
          showicon="true"
        >
          <img slot="icon" src="assets/icons/statuses/muted/${badge.icon}" alt="${badge.label} icon"/>
        </core-badge>
      </div>`;
  }

  private getDatesHTML(dates: BaseBoundaryDates): string {
    return `
          <div class="study-gantt-chart-tooltip-item">
            <span class="study-gantt-chart-tooltip-label">Date</span>
            <span class="study-gantt-chart-tooltip-value">Baseline: ${this.commonFunctionsService.formatDate(dates.baseline)}</span>
            <span class="study-gantt-chart-tooltip-value">Planned: ${this.commonFunctionsService.formatDate(dates.planned)}</span>
            <span class="study-gantt-chart-tooltip-value">Actual: ${this.commonFunctionsService.formatDate(dates.actual)}</span>
          </div>
      `;
  }

  private getDeltaHTML(delta: string): string {
    const [bold, regular] = delta.split(':');
    const boldPart = `<span class="study-gantt-chart-tooltip-highlight-value">${regular ? bold + ':' : bold}</span>`;

    const displayDeltaValue = regular ? `${boldPart}${regular}` : boldPart;

    return `<div class="study-gantt-chart-tooltip-item">
            <span class="study-gantt-chart-tooltip-value">
            ${displayDeltaValue}
            </span>
          </div>`;
  }

  private getMilestoneBadge(milestoneStatus: MilestoneStatus): BadgeConfig {
    const STUDY_MILESTONE_BADGES: Record<MilestoneStatus, BadgeConfig> = {
      Completed: {
        label: 'Completed | Timing unknown',
        icon: 'completed.svg',
        ...BADGES_COLORS.transparent_blue_text
      },
      'Completed | Accelerated': {
        label: 'Completed | Accelerated',
        icon: 'completed.svg',
        ...BADGES_COLORS.transparent_blue_text
      },
      'Completed | On Time': {
        label: 'Completed | On time',
        icon: 'completed.svg',
        ...BADGES_COLORS.transparent_blue_text
      },
      'Completed | Late': {
        label: 'Completed | Late',
        icon: 'completed.svg',
        ...BADGES_COLORS.transparent_blue_text
      },
      'Past Due': {
        label: 'Past due',
        icon: 'past-due.svg',
        ...BADGES_COLORS.transparent_red_text
      },

      Upcoming: {
        label: 'Upcoming',
        icon: 'upcoming.svg',
        ...BADGES_COLORS.transparent_gray_text
      },
      'On Track': {
        label: 'On track',
        icon: 'on-track.svg',
        ...BADGES_COLORS.transparent_green_text
      },
      'At Risk': {
        label: 'At risk',
        icon: 'at-risk.svg',
        ...BADGES_COLORS.orange
      }
    };
    return STUDY_MILESTONE_BADGES[milestoneStatus];
  }
}
